import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { Dialog } from "@headlessui/react";
import { toasts } from "@/src/components/toasts/toasts";
import { useQueryClient } from "@tanstack/react-query";
import { 
  Question as GqlQuestion, 
  useUpdateQuestionMutation, 
  useDdqlQuery 
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";

// Custom interface extending the UpdateQuestionInput to include ddqlID
interface MoveQuestionInput {
  input: {
    id: string;
    ddqlID: string;
  };
}

interface MoveQuestionToDDQLModalProps {
  open: boolean;
  onClose: () => void;
  question: GqlQuestion | null;
  targetDdqlId: string | null;
  targetDdqlName: string | null;
}

export function MoveQuestionToDDQLModal({
  open,
  onClose,
  question,
  targetDdqlId,
  targetDdqlName,
}: MoveQuestionToDDQLModalProps) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateQuestion = useUpdateQuestionMutation(client);
  const activeDealId = useSelector(authSelectors.activeDealId);
  
  const targetDdqlQuery = useDdqlQuery(
    client,
    {
      id: targetDdqlId || "",
      dealId: activeDealId || "",
    },
    {
      enabled: !!targetDdqlId && !!activeDealId && open,
    }
  );

  const handleMove = () => {
    if (!question || !targetDdqlId) return;

    // Don't move a question to its current DDQL
    if (question.ddql?.id === targetDdqlId) {
      toasts.info("Question is already in this list");
      onClose();
      return;
    }
    
    if (targetDdqlQuery.data?.ddql.children && targetDdqlQuery.data.ddql.children.length > 0) {
      toasts.error("Cannot move question to a list with sublists");
      onClose();
      return;
    }

    updateQuestion.mutate(
      {
        input: {
          id: question.id,
          ddqlID: targetDdqlId
        }
      } as MoveQuestionInput,
      {
        onSuccess: () => {
          toasts.success(`Question moved to ${targetDdqlName}`);
          
          queryClient.invalidateQueries({
            queryKey: ["Questions"],
          });
          
          queryClient.invalidateQueries({
            queryKey: ["Ddql"],
          });
          
          if (question.ddql?.id) {
            queryClient.invalidateQueries({
              queryKey: ["Ddql", { id: question.ddql.id }],
            });
            
            if (activeDealId) {
              queryClient.invalidateQueries({
                queryKey: ["Ddql", { id: question.ddql.id, dealId: activeDealId }],
              });
            }
          }
          
          if (targetDdqlId) {
            queryClient.invalidateQueries({
              queryKey: ["Ddql", { id: targetDdqlId }],
            });
            
            if (activeDealId) {
              queryClient.invalidateQueries({
                queryKey: ["Ddql", { id: targetDdqlId, dealId: activeDealId }],
              });
            }
          }
          
          onClose();
        },
        onError: (error) => {
          console.error("Error moving question:", error);
          toasts.error("Failed to move question");
        }
      }
    );
  };

  const isLoading = targetDdqlQuery.isLoading && open;
  
  const hasChildDdqls = targetDdqlQuery.data?.ddql.children && 
                         targetDdqlQuery.data.ddql.children.length > 0;

  return (
    <AnimatedModal open={open} onClose={onClose} size="md">
      <div>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-persian-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon
              className="h-6 w-6 text-persian-600"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              Move Question
            </Dialog.Title>
            <div className="mt-2">
              {hasChildDdqls ? (
                <p className="text-sm text-red-500">
                  Cannot move question to a list with sublists
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  Are you sure you want to move this question to the "{targetDdqlName}" list?
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="mt-8 sm:mt-12 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-persian-800/90 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-persian-700/90 ring-1 ring-inset ring-persian-300 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleMove}
            disabled={isLoading || hasChildDdqls || updateQuestion.isPending}
          >
            {updateQuestion.isPending ? "Moving..." : isLoading ? "Loading..." : "Move Question"}
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
} 